import { IonButton, IonCard, IonCardContent } from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { Formik } from 'formik';
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { pdlValidatedAtom } from '../../../store/Atoms';
import { trackActionsRequest } from '../../../store/Requests';
import DefaultInput from '../defaultinput/DefaultInput';

const PdlSchema = (preFilledPdl?: string | false) =>
    Yup.object().shape({
        pdl: Yup.string()
            .matches(/^\d+$/, t`PDL must be a number`)
            .length(14, t`PDL must be 14 characters long`)
            .required(t`PDL is required`)
            .test({
                name: 'preFilledPdl',
                exclusive: false,
                params: {},
                message: t`PDL does not match the one saved in Odoo`,
                test: function (value) {
                    return preFilledPdl ? value === preFilledPdl : true;
                },
            }),
        confirmPdl: Yup.string()
            .matches(/^\d+$/, t`PDL must be a number`)
            .oneOf([Yup.ref('pdl')], t`PDL's must be the same!`)
            .required(t`Confirm PDL is required`),
    });

/*
  PDL = French / electric network term: "Point De Livraison"
  (english: metering point number)
  Used for meter identification and important to validate for the grid.

  If we already have the PDL, installer has to enter the PDL and validate.
  If we don't have the PDL, installer has to enter PDL twice.
  If validated: display OK feedback, if not display NOK feedback.
  If it's already validated, display OK feedback.
*/

type PDLFormProps = {
    preFilledPdl?: string | false;
    pdl: string;
    onChange: (meteringPointNumber: string) => void;
};

const PDLForm: React.FC<PDLFormProps> = ({ preFilledPdl, pdl, onChange }) => {
    const [showPdl, setShowPdl] = useState(false);
    const [pdlValidated, setPdlValidated] = useAtom(pdlValidatedAtom);

    const handleFormSubmit = useCallback(
        ({ pdl, confirmPdl }: { pdl: string; confirmPdl: string }) => {
            setPdlValidated(pdl === confirmPdl);
            onChange(pdl);
            trackActionsRequest('validate_pdl', {
                pdl,
                confirmPdl,
            });
        },
        [onChange, setPdlValidated]
    );

    const onPDLBlurHandler = useCallback((e: CustomEvent<FocusEvent>) => {
        setShowPdl(false);
    }, []);

    const onPDLFocusHandler = useCallback((e: CustomEvent<FocusEvent>) => {
        setShowPdl(true);
    }, []);

    return (
        <Formik
            initialValues={{ pdl: pdl, confirmPdl: preFilledPdl || '' }}
            validationSchema={PdlSchema(preFilledPdl)}
            onSubmit={handleFormSubmit}
        >
            {({
                values,
                handleChange,
                errors,
                touched,
                isValid,
                resetForm,
                submitForm,
            }) => (
                <>
                    <DefaultInput
                        className={classNames({
                            'ion-invalid': errors.pdl,
                            'ion-touched': touched.pdl,
                        })}
                        name="pdl"
                        value={values.pdl}
                        label={t`Customer's PDL`}
                        type={showPdl ? 'text' : 'password'}
                        clearOnEdit={false}
                        disabled={pdlValidated}
                        placeholder={t`14 digit number`}
                        maxlength={14}
                        onIonInput={handleChange}
                        errorText={errors.pdl}
                        onIonFocus={onPDLFocusHandler}
                        onIonBlur={onPDLBlurHandler}
                        data-tiko-test="pdl-field"
                        onPaste={(e) => e.preventDefault()}
                        onCopy={(e) => e.preventDefault()}
                    ></DefaultInput>
                    {!pdlValidated && !preFilledPdl && (
                        <DefaultInput
                            className={classNames({
                                'ion-invalid': errors.confirmPdl,
                                'ion-touched': touched.confirmPdl,
                            })}
                            name="confirmPdl"
                            value={values.confirmPdl}
                            label={t`Confirm PDL`}
                            type="text"
                            onIonChange={handleChange}
                            placeholder={t`14 digit number`}
                            maxlength={14}
                            onPaste={(e) => e.preventDefault()}
                            onCopy={(e) => e.preventDefault()}
                        ></DefaultInput>
                    )}
                    {pdlValidated ? (
                        <>
                            <IonCard
                                color="success"
                                className="ion-no-margin ion-margin-top"
                            >
                                <IonCardContent>
                                    <Trans>PDL Validated Successfully</Trans>
                                </IonCardContent>
                            </IonCard>
                            <IonButton
                                className="ion-margin-top ion-margin-bottom"
                                expand="block"
                                onClick={() => {
                                    resetForm();
                                    setPdlValidated(false);
                                    onChange('');
                                }}
                                data-tiko-test="revalidate-pdl-button"
                            >
                                <Trans>Validate PDL Again</Trans>
                            </IonButton>
                        </>
                    ) : (
                        <IonButton
                            className="ion-margin-top ion-margin-bottom"
                            expand="block"
                            onClick={submitForm}
                            disabled={!isValid}
                            data-tiko-test="validate-pdl-button"
                        >
                            <Trans>Validate PDL</Trans>
                        </IonButton>
                    )}
                </>
            )}
        </Formik>
    );
};

export default PDLForm;
